import { useEffect, useState } from "react";

import groupBy from "lodash/groupBy";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import type { InjectedRouter } from "react-router";

import { PaginationControls } from "@ps-design-system/components/Pagination";

import { fetchOwnerVendorsInfo } from "../app-main/actions/vendors";
import { selectListingsData } from "../app-main/selectors/listings.selector";
import {
  selectProfilePhoneVerified,
  selectProfileSSOid,
} from "../app-main/selectors/profile.selectors";
import {
  selectVendorsData,
  selectVendorsHasFetched,
  selectVendorsIsFetching,
} from "../app-main/selectors/vendors.selectors";
import { EditVerifyPhoneModal } from "../app-profile/EditVerifyPhoneModal";
import ModalManager from "../elements-modal/modal-manager-v2";

import DraftSpacePanel from "./DraftSpacePanel";
import hoc from "./hoc";
import LiveSpacePanel from "./LiveSpacePanel";
import DeleteSpaceModal from "./modals/DeleteSpaceModal";
import MyListingsShareModal from "./modals/MyListingsShareModal";
import { getVendorFilterFromLocation } from "./MyListings.utils";
import MyListingBanners from "./MyListingsBanner";
import NoListingsPanel from "./NoListingsPanel";
import { ResultCountLabel } from "./ResultCountLabel";
import SpacesServicesFooter from "./SpacesServicesFooter";
import SpacesServicesHeader from "./SpacesServicesHeader";
import { MyVendors } from "./Vendors";

type MyListingsProps = {
  /** Statsig-gated: shows the spaces/services filters and the vendor panels. */
  allowVendorEdits?: boolean;
  currentPage: number;
  /** Forwarded to MyListingBanners, which decides on the payout-setup banner. */
  managedAccount?: Record<string, unknown>;
  modal?: {
    modalId?: string;
    modalData?: {
      listing?: Record<string, unknown>;
      spaceId?: string;
    };
  };
  location?: { pathname?: string; query?: Record<string, string> };

  pageSize: number;
  router?: InjectedRouter;
  showAddonsTooltip?: boolean;
  showTooltip?: boolean;
  spaces?: {
    data?: {
      _id: string;
      cancellation_policy?: { name?: string };
      completion_percentage?: number;
      /** Metro code, e.g. `CHI` — the vendor-edits gate is Chicago-only. */
      metro?: string;
      status?: string;
      title?: string;
    }[];
  };
  totalPages?: number;
  totalSpaces?: number;
  workflows?: {
    data?: {
      results_by_space?: Record<
        string,
        {
          date_modified: string;
          host_update_url?: string;
          listing_id?: string;
          obj_state?: string;
        }[]
      >;
    };
  };
  onPageChange?: (page: number) => void;
  toggleModal: (modalId: string, modalData?: Record<string, unknown>) => void;
  updateNux?: (experienceName: string) => void;
};

/** The host's listings dashboard: one panel per space, paginated. */
const MyListings = ({
  allowVendorEdits,
  currentPage,
  location,
  pageSize,
  router,
  showAddonsTooltip,
  showTooltip,
  updateNux,
  ...props
}: MyListingsProps) => {
  // spaces-v2 returns this page already sorted newest-first; render as-is.
  const spaces = props.spaces?.data ?? [];
  const totalPages = props.totalPages ?? 1;
  const totalSpaces = props.totalSpaces ?? spaces.length;
  const modalId = props.modal?.modalId ?? "";
  const rangeStart = 0 === totalSpaces ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalSpaces);
  const listingsData = useSelector(selectListingsData);
  const listingIndexedBySpaceId = groupBy(listingsData ?? [], "parentSpaceId");
  const firstActiveSpace = spaces.find((space) => "DRAFT" !== space.status);
  const workflowsResults = props.workflows?.data?.results_by_space ?? {};

  const isPhoneVerified = useSelector(selectProfilePhoneVerified);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);

  // Hosts need a verified number to manage listings, so prompt as soon as the
  // profile resolves with an unverified one.
  useEffect(() => {
    if (!isPhoneVerified) {
      setIsPhoneModalOpen(true);
    }
  }, [isPhoneVerified]);

  const dispatch = useDispatch();
  const SSOid = useSelector(selectProfileSSOid);
  const vendors = useSelector(selectVendorsData);
  const vendorsHasFetched = useSelector(selectVendorsHasFetched);
  const isFetchingVendors = useSelector(selectVendorsIsFetching);
  const activeFilter = getVendorFilterFromLocation(location);

  // Fetch vendors once the SSOid is known, but only when vendor panels are
  // actually shown — otherwise this fires an unnecessary request for hosts
  // who aren't vendors.
  useEffect(() => {
    if (allowVendorEdits && SSOid && !isFetchingVendors && !vendorsHasFetched) {
      dispatch(fetchOwnerVendorsInfo(SSOid));
    }
  }, [allowVendorEdits, dispatch, vendorsHasFetched, isFetchingVendors, SSOid]);

  const selectFilter = (filter: string) => {
    if (!router) {
      return;
    }

    const pathname = location?.pathname || "";
    const query = location?.query || {};

    router.push({
      pathname,
      query: { ...query, filter },
    });
  };

  const isHost = 0 < spaces.length;

  const showSpaces =
    isHost && (!allowVendorEdits || "services" !== activeFilter);
  const showVendors = allowVendorEdits && "spaces" !== activeFilter;

  return (
    <div className="mylistings-page">
      <MyListingBanners {...props} />
      <div className="ps-container-lg-margin ps-container-fixed">
        <div className="ps-row flex column">
          <div className="col-xs-12 margin-lg-top">
            {!allowVendorEdits && (
              <div className="mylistings-header flex bottom space-between">
                <h2>
                  <FormattedMessage
                    id="hosting.my_listings.banner_heading"
                    defaultMessage="Listings"
                  />
                </h2>
                <div className="addspace-btn-container flex">
                  <div className="margin-md-left">
                    <a
                      href="/your-space"
                      className={`btn ${!spaces.length ? "btn-primary" : "btn-gray-outline btn-outlined-light"}`}
                    >
                      <FormattedMessage
                        id="hosting.my_listings.add_space_link_text"
                        defaultMessage="Add a space"
                      />
                    </a>
                  </div>
                </div>
              </div>
            )}
            {allowVendorEdits && (
              <SpacesServicesHeader
                activeFilter={activeFilter}
                onSelectFilter={selectFilter}
              />
            )}
            {0 < totalSpaces && !allowVendorEdits && (
              <ResultCountLabel
                start={rangeStart}
                end={rangeEnd}
                total={totalSpaces}
              />
            )}
            {0 === totalSpaces &&
              (!allowVendorEdits || "spaces" === activeFilter) && (
                <NoListingsPanel />
              )}
            {showSpaces &&
              0 < spaces.length &&
              spaces.map((space) => {
                const { _id = "" } = space || {};
                const spaceOrder =
                  spaces.findIndex((el) => el._id === space._id) + 1;
                const title = space.title || (
                  <FormattedMessage
                    id="hosting.my_listings.new_space_title"
                    defaultMessage="New Space {spaceOrder}"
                    values={{ spaceOrder: spaces.length > 1 ? spaceOrder : "" }}
                  />
                );
                if ("DRAFT" === space.status) {
                  return (
                    <DraftSpacePanel
                      key={_id}
                      _id={_id}
                      cancellation_policy={space.cancellation_policy}
                      completion_percentage={space.completion_percentage}
                      toggleModal={props.toggleModal}
                      // DraftSpacePanel is still untyped JS, so TS infers
                      // `title: string` from its `title = ''` default. Its
                      // propTypes accept a node, which is what the untitled
                      // fallback is. Drop the cast once it moves to TSX.
                      title={title as string}
                    />
                  );
                }
                return (
                  <LiveSpacePanel
                    key={_id}
                    space={space}
                    listings={listingIndexedBySpaceId[_id] ?? []}
                    modalId={modalId}
                    toggleModal={props.toggleModal}
                    showTooltip={
                      showTooltip && firstActiveSpace?._id === space._id
                    }
                    updateNux={updateNux}
                    showAddonsTooltip={showAddonsTooltip}
                    workflowBySpace={workflowsResults[_id]}
                    allowVendorEdits={allowVendorEdits}
                  />
                );
              })}
            {showVendors && (
              <MyVendors
                vendors={vendors}
                activeFilter={activeFilter}
                hasFetched={vendorsHasFetched}
                hasListings={!!totalSpaces}
              />
            )}
            {totalPages > 1 && (
              <div className="mylistings-footer flex space-between middle margin-lg-top">
                <ResultCountLabel
                  start={rangeStart}
                  end={rangeEnd}
                  total={totalSpaces}
                />
                <PaginationControls
                  className="tw-w-auto tw-mx-0"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={props.onPageChange}
                />
              </div>
            )}
            {allowVendorEdits && (
              <SpacesServicesFooter activeFilter={activeFilter} />
            )}
          </div>
          <ModalManager whichModal={modalId} toggleModal={props.toggleModal}>
            <DeleteSpaceModal
              id="DeleteSpaceModal"
              spaceId={props.modal?.modalData?.spaceId ?? ""}
              {...props}
            />
            <MyListingsShareModal
              id="MyListingsShareModal"
              listingData={props.modal?.modalData?.listing ?? {}}
            />
          </ModalManager>
          {isPhoneModalOpen && (
            <EditVerifyPhoneModal
              open={isPhoneModalOpen}
              variant="marketing"
              onOpenChange={setIsPhoneModalOpen}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export type { MyListingsProps };

export default hoc(MyListings);
